import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Title2, Text, Button } from '@fluentui/react-components';
import { ShieldError24Regular } from '@fluentui/react-icons';
import { usePermissions, type Permission } from '../hooks/usePermission';
import { useAuthStore } from '../store/auth.store';
import { pickLandingPath } from '../utils/defaultRoute';
import { useT } from '../i18n';

/**
 * Guards a route by permission. Renders `children` only if the current
 * user (per `usePermission`) has the required permission; otherwise shows
 * an explicit "acceso denegado" screen with a way back to the dashboard.
 *
 * This is a real client-side authorization check (in addition to backend
 * guards) — routes must NOT rely solely on hiding links in the Sidebar,
 * since a user can navigate directly to the URL.
 */
export function ProtectedRoute({
  requiredPermission,
  children,
}: {
  requiredPermission: Permission;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { permissions, isLoading } = usePermissions();
  const activeRole = useAuthStore(s => s.user?.activeRole);
  const allowed = permissions.has(requiredPermission);
  const t = useT();

  // Mientras llega la matriz de permisos todo parece denegado: no se muestra
  // "Acceso denegado" a quien sí tiene el permiso.
  if (isLoading) return null;

  if (!allowed) {
    // Si el rol no puede ver el dashboard, el botón lleva a una pantalla que sí puede ver.
    const backPath = pickLandingPath(permissions, activeRole);
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 16,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <ShieldError24Regular style={{ fontSize: 48 }} />
        <Title2>{t.protectedRoute.title}</Title2>
        <Text>{t.protectedRoute.message}</Text>
        <Button appearance="primary" onClick={() => navigate(`/${tenantId ?? 'demo'}/${backPath}`)}>
          {t.protectedRoute.dashboardBtn}
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
